import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { Bike, Package, ArrowRight, ChevronLeft } from 'lucide-react';
import { useServicePalette } from '../hooks/useServicePalette';
import logo from "../media/cy-logo-negative.png";
import logotk from "../media/cy_take_away.png";

export function OrderModePage() {
    const { tableId } = useParams<{ tableId: string }>();
    const navigate = useNavigate();
    const { setOrderMode, isTakeAway, config } = useAuthStore();
    const palette = useServicePalette();

    useEffect(() => {
        if (isTakeAway === false) {
            navigate(`/dashboard/${tableId}`, { replace: true });
            return;
        }

        if (!config) return;

        const onlyDelivery = config.accept_delivery && !config.accept_takeaway;
        const onlyTakeaway = config.accept_takeaway && !config.accept_delivery;

        if (onlyDelivery) {
            setOrderMode('delivery');
            navigate(`/dashboard/${tableId}`, { replace: true });
        } else if (onlyTakeaway) {
            setOrderMode('pickup');
            navigate(`/dashboard/${tableId}`, { replace: true });
        }
    }, [isTakeAway, config, setOrderMode, navigate, tableId]);

    const handleSelect = (mode: 'delivery' | 'pickup') => {
        setOrderMode(mode);
        navigate(`/dashboard/${tableId}`);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 py-12">
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl max-w-md w-full border border-gray-100 dark:border-gray-700">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm mb-6 transition-colors"
                >
                    <ChevronLeft className="w-4 h-4" />
                    Volver
                </button>

                <div className="text-center mb-10">
                    <div className="relative mx-auto mb-6 w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 p-1">
                        <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                            <img src={isTakeAway ? logotk : logo} alt="Logo" className="w-16 h-16 object-contain" />
                        </div>
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-2">
                        ¿Cómo querés tu pedido?
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Seleccioná la modalidad de entrega
                    </p>
                </div>

                <div className="grid gap-4">
                    {config?.accept_delivery && (
                        <button
                            onClick={() => handleSelect('delivery')}
                            className="group relative bg-white dark:bg-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl p-6 flex items-center gap-4 transition-all duration-300 hover:scale-[1.02] shadow-sm hover:shadow-md"
                        >
                            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${palette.primaryFrom} ${palette.primaryTo} text-white flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                <Bike className="w-8 h-8" />
                            </div>
                            <div className="flex-1 text-left">
                                <h3 className="font-bold text-gray-900 dark:text-white text-lg">Envío a domicilio</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Te lo enviamos donde estés</p>
                            </div>
                            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                        </button>
                    )}

                    {config?.accept_takeaway && (
                        <button
                            onClick={() => handleSelect('pickup')}
                            className="group relative bg-white dark:bg-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl p-6 flex items-center gap-4 transition-all duration-300 hover:scale-[1.02] shadow-sm hover:shadow-md"
                        >
                            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${palette.primaryFrom} ${palette.primaryTo} text-white flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                <Package className="w-8 h-8" />
                            </div>
                            <div className="flex-1 text-left">
                                <h3 className="font-bold text-gray-900 dark:text-white text-lg">Retiro en local</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Pasás a buscarlo vos</p>
                            </div>
                            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
